import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  CalendarDays,
  Flame,
  Sparkles,
  Target,
} from "lucide-react";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  flattenProductRoadmapDays,
  withProductRoadmapDates,
} from "../utils/buildProductRoadmap";

const groupDaysByMonth = (days) => {
  const map = {};
  days.forEach((day) => {
    const dateObj = new Date(day.date);
    const key = dateObj.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
    if (!map[key]) map[key] = [];
    map[key].push(day);
  });
  return map;
};

const getMonthCells = (days) => {
  if (!days.length) return [];
  const firstDate = new Date(days[0].date);
  const startDay = firstDate.getDay();
  const cells = [];
  for (let i = 0; i < startDay; i += 1) cells.push(null);
  days.forEach((day) => cells.push(day));
  return cells;
};

const formatHumanDate = (date) =>
  new Date(date).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

const isFutureDay = (day) => day?.status === "upcoming";

export default function ProductCalendarViewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [roadmap, setRoadmap] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const [hasProAccess, setHasProAccess] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;

    const load = async () => {
      try {
        setLoading(true);
        const [userSnap, roadmapSnap] = await Promise.all([
          getDoc(doc(db, "users", user.uid)),
          getDoc(doc(db, "product_roadmaps", user.uid)),
        ]);

        setHasProAccess(userSnap.exists() && userSnap.data()?.plan === "PRO");
        setRoadmap(roadmapSnap.exists() ? roadmapSnap.data() : null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.uid]);

  const calendarDays = useMemo(() => {
    if (!roadmap?.roadmapRef) return [];

    const flat = flattenProductRoadmapDays(roadmap.roadmapRef);
    return withProductRoadmapDates(
      flat,
      roadmap.consistency?.startedAt,
      roadmap.progress?.completedDays || {}
    );
  }, [roadmap]);

  const stats = useMemo(() => {
    const summary = {
      total: calendarDays.length,
      completed: 0,
      today: 0,
      missed: 0,
      upcoming: 0,
    };

    calendarDays.forEach((day) => {
      if (day.status === "completed") summary.completed += 1;
      else if (day.status === "today") summary.today += 1;
      else if (day.status === "missed") summary.missed += 1;
      else summary.upcoming += 1;
    });

    return summary;
  }, [calendarDays]);

  const monthGroups = useMemo(() => groupDaysByMonth(calendarDays), [calendarDays]);

  const markComplete = async (day) => {
    if (!user?.uid || !day?.dateKey || day.completed) return;

    await updateDoc(doc(db, "product_roadmaps", user.uid), {
      [`progress.completedDays.${day.dateKey}`]: true,
    });

    setRoadmap((prev) => ({
      ...prev,
      progress: {
        ...(prev?.progress || {}),
        completedDays: {
          ...(prev?.progress?.completedDays || {}),
          [day.dateKey]: true,
        },
      },
    }));
    setActiveDay(null);
  };

  if (loading) {
    return (
      <StatePage>
        <StateShell>
          <LoadingHeroCard>
            <SkeletonPill />
            <SkeletonTitle />
            <SkeletonText $w="68%" />
            <SkeletonText $w="54%" />
            <LoadingStatGrid>
              {Array.from({ length: 4 }).map((_, index) => (
                <LoadingStatCard key={index}>
                  <SkeletonText $w="42%" />
                  <SkeletonValue />
                  <SkeletonText $w="78%" />
                </LoadingStatCard>
              ))}
            </LoadingStatGrid>
          </LoadingHeroCard>

          <LoadingMonthCard>
            <SkeletonSectionTitle />
            <LoadingCalendarGrid>
              {Array.from({ length: 14 }).map((_, index) => (
                <LoadingDayCell key={index} />
              ))}
            </LoadingCalendarGrid>
          </LoadingMonthCard>
        </StateShell>
      </StatePage>
    );
  }

  if (!roadmap) {
    return (
      <StatePage>
        <StateCard>
          <Eyebrow>
            <CalendarDays size={14} />
            Product calendar
          </Eyebrow>
          <h1>No product roadmap yet</h1>
          <p>Create your product roadmap first, then plan the build by actual dates.</p>
          <ActionButton onClick={() => navigate("/create-product-roadmap")}>
            Create Product Roadmap
          </ActionButton>
        </StateCard>
      </StatePage>
    );
  }

  if (!hasProAccess) {
    return (
      <StatePage>
        <StateCard>
          <Eyebrow>
            <Sparkles size={14} />
            PRO required
          </Eyebrow>
          <h1>Product calendar view is part of PRO</h1>
          <p>Upgrade to see your product roadmap on a real calendar with day-by-day build visibility.</p>
          <ActionButton onClick={() => navigate("/pricing")}>View Pricing</ActionButton>
        </StateCard>
      </StatePage>
    );
  }

  return (
    <Page>
      <HeaderShell>
        <HeaderTop>
          <div>
            <Eyebrow>
              <Sparkles size={14} />
              Product Calendar View
            </Eyebrow>
            <Title>See your self internship on real dates</Title>
            <HeaderSub>
              Track every product build day on a calendar, spot missed momentum, and jump into the exact task or AI help you need next.
            </HeaderSub>
          </div>
          <FocusPanel>
            <FocusLabel>Consistency Sync</FocusLabel>
            <FocusValue>{roadmap.calendarReminderTime || "07:30"}</FocusValue>
            <FocusHint>
              Your daily product build reminder time for planning, focus, and execution.
            </FocusHint>
          </FocusPanel>
        </HeaderTop>

        <StatsGrid>
          <StatCard>
            <StatLabel>
              <CalendarDays size={14} />
              Total days
            </StatLabel>
            <StatValue>{stats.total}</StatValue>
            <StatHint>Every scheduled product build day across the roadmap.</StatHint>
          </StatCard>
          <StatCard>
            <StatLabel>
              <CheckCircle2 size={14} />
              Completed
            </StatLabel>
            <StatValue>{stats.completed}</StatValue>
            <StatHint>Build days already finished and marked done.</StatHint>
          </StatCard>
          <StatCard>
            <StatLabel>
              <Flame size={14} />
              Active today
            </StatLabel>
            <StatValue>{stats.today}</StatValue>
            <StatHint>The current build day that deserves your full focus.</StatHint>
          </StatCard>
          <StatCard>
            <StatLabel>
              <AlertTriangle size={14} />
              Missed
            </StatLabel>
            <StatValue>{stats.missed}</StatValue>
            <StatHint>Use these as recovery slots, not as guilt markers.</StatHint>
          </StatCard>
        </StatsGrid>

        <LegendRow>
          <LegendPill $tone="today">Today</LegendPill>
          <LegendPill $tone="completed">Completed</LegendPill>
          <LegendPill $tone="missed">Missed</LegendPill>
          <LegendPill $tone="pending">Upcoming</LegendPill>
          <LegendCopy>
            Tap any day to open your product roadmap or task board.
          </LegendCopy>
        </LegendRow>
      </HeaderShell>

      {Object.entries(monthGroups).map(([month, days]) => {
        const monthCells = getMonthCells(days);
        const monthCompleted = days.filter((day) => day.status === "completed").length;
        const monthToday = days.find((day) => day.status === "today");
        return (
          <MonthSection key={month}>
            <MonthHeader>
              <div>
                <MonthTitle>{month}</MonthTitle>
                <MonthSub>
                  {monthCompleted}/{days.length} completed
                  {monthToday ? ` • Day ${monthToday.absoluteIndex + 1} is live today` : ""}
                </MonthSub>
              </div>
              <MonthBadge>
                <Target size={14} />
                {days.length} build days
              </MonthBadge>
            </MonthHeader>

            <GridScroller>
              <Grid>
                {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                  <DayHeader key={d}>{d}</DayHeader>
                ))}
                {monthCells.map((day, idx) =>
                  day ? (
                    <DayCell
                      key={`${day.id}-${day.dateKey}`}
                      status={day.status}
                      onClick={() => setActiveDay(day)}
                    >
                      <TopRow>
                        <DateNumber>{String(new Date(day.date).getDate()).padStart(2, "0")}</DateNumber>
                        <DayCount>Day {day.absoluteIndex + 1}</DayCount>
                      </TopRow>
                      <Topic>{day.title}</Topic>
                      <MotivationText status={day.status}>
                        {day.status === "completed" && "Marked done"}
                        {day.status === "today" && "Today’s mission"}
                        {day.status === "missed" && "Needs recovery"}
                        {day.status === "upcoming" && "Upcoming"}
                      </MotivationText>
                    </DayCell>
                  ) : (
                    <EmptyCell key={`empty-${idx}`} />
                  )
                )}
              </Grid>
            </GridScroller>
          </MonthSection>
        );
      })}

      {activeDay && (
        <Overlay status={activeDay.status} onClick={() => setActiveDay(null)}>
          <DetailCard onClick={(e) => e.stopPropagation()}>
            <DetailHeader>
              <HeaderLeft>
                <strong>
                  {activeDay.status === "completed" && "🎉 Well Done!"}
                  {activeDay.status === "today" && "🚀 Today’s Build"}
                  {activeDay.status === "missed" && "⚠ Recovery Build"}
                  {activeDay.status === "upcoming" && "🔒 Upcoming Build"}
                </strong>
                <DateText>{formatHumanDate(activeDay.date)}</DateText>
              </HeaderLeft>
              <CloseBtn onClick={() => setActiveDay(null)}>✕</CloseBtn>
            </DetailHeader>

            <DetailBody>
              <TopicTitle>{activeDay.title}</TopicTitle>

              <MotivationText status={activeDay.status}>
                {activeDay.status === "completed" &&
                  "You shipped this build day. Keep stacking proof of work."}
                {activeDay.status === "today" &&
                  "Stay narrow today. Finish this feature slice properly."}
                {activeDay.status === "missed" &&
                  "Recovery is part of shipping. Re-enter with a smaller step."}
                {activeDay.status === "upcoming" &&
                  "This build unlocks next. Let today stay focused."}
              </MotivationText>

              <DetailMetaGrid>
                <DetailMetaCard>
                  <DetailMetaLabel>
                    <Target size={14} />
                    Feature focus
                  </DetailMetaLabel>
                  <DetailMetaValue>{activeDay.weekFeatureFocus || activeDay.topic || "Current build task"}</DetailMetaValue>
                </DetailMetaCard>

                <DetailMetaCard>
                  <DetailMetaLabel>
                    <Clock3 size={14} />
                    Objective
                  </DetailMetaLabel>
                  <DetailMetaValue>{activeDay.objective}</DetailMetaValue>
                </DetailMetaCard>
              </DetailMetaGrid>

              <MetaRow>
                <StatusTag status={activeDay.status}>
                  {activeDay.status.toUpperCase()}
                </StatusTag>
                <StatusTag status="pending">DAY {activeDay.absoluteIndex + 1}</StatusTag>
              </MetaRow>
            </DetailBody>

            <DetailActions>
              <PrimaryBtn onClick={() => navigate("/my-product-roadmap")}>
                📦 Open Product Roadmap
              </PrimaryBtn>
              <SecondaryBtn
                onClick={() =>
                  navigate(`/product-tasks-board/day?dayId=${activeDay.id}`)
                }
              >
                🧩 View Task Board
              </SecondaryBtn>
              {!isFutureDay(activeDay) && activeDay.status !== "completed" && (
                <PrimaryBtn
                  style={{ background: "#22c55e", color: "#03111e" }}
                  onClick={() => markComplete(activeDay)}
                >
                  ✔ Mark Day Complete
                </PrimaryBtn>
              )}
            </DetailActions>
          </DetailCard>
        </Overlay>
      )}
    </Page>
  );
}

const StatePage = styled.div`
  min-height: 100vh;
  padding: 120px 20px 64px;
  display: flex;
  justify-content: center;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
  color: #e2e8f0;
`;

const StateCard = styled.div`
  width: 100%;
  max-width: 760px;
  padding: 28px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.14);

  h1 {
    margin: 16px 0 0;
    color: #f8fafc;
  }

  p {
    margin: 12px 0 0;
    color: #94a3b8;
    line-height: 1.7;
  }
`;

const StateShell = styled.div`
  width: 100%;
  max-width: 1140px;
`;

const shimmer = `
  background: linear-gradient(
    90deg,
    rgba(148, 163, 184, 0.08) 0%,
    rgba(148, 163, 184, 0.18) 50%,
    rgba(148, 163, 184, 0.08) 100%
  );
  background-size: 200% 100%;
  animation: loadingShimmer 1.4s ease-in-out infinite;
`;

const LoadingHeroCard = styled.div`
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const LoadingMonthCard = styled.div`
  margin-top: 28px;
  padding: 20px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
`;

const LoadingStatGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingStatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const LoadingCalendarGrid = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(7, minmax(44px, 1fr));
  gap: 10px;
`;

const LoadingDayCell = styled.div`
  min-height: 120px;
  border-radius: 16px;
  ${shimmer}
`;

const SkeletonPill = styled.div`
  width: 150px;
  height: 30px;
  border-radius: 999px;
  ${shimmer}
`;

const SkeletonTitle = styled.div`
  width: min(520px, 88%);
  height: 46px;
  margin-top: 18px;
  border-radius: 18px;
  ${shimmer}
`;

const SkeletonSectionTitle = styled.div`
  width: min(260px, 50%);
  height: 30px;
  border-radius: 14px;
  ${shimmer}
`;

const SkeletonValue = styled.div`
  width: 86px;
  height: 30px;
  margin-top: 10px;
  border-radius: 12px;
  ${shimmer}
`;

const SkeletonText = styled.div`
  width: ${({ $w = "100%" }) => $w};
  height: 14px;
  margin-top: 12px;
  border-radius: 999px;
  ${shimmer}
`;

const ActionButton = styled.button`
  margin-top: 20px;
  padding: 12px 18px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #111827;
  font-weight: 900;
  cursor: pointer;
`;

const Page = styled.div`
  padding: 116px 20px 64px;
  min-height: 100vh;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;
const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;
const HeaderShell = styled.div`
  max-width: 1140px;
  margin: 0 auto 28px;
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;
const HeaderTop = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(260px, 0.9fr);
  gap: 18px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const Title = styled.h1`
  margin: 14px 0 0;
  font-size: 38px;
  line-height: 1.05;
  font-weight: 1000;
  color: #f8fafc;
`;
const HeaderSub = styled.p`
  margin: 12px 0 0;
  max-width: 760px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.7;
`;
const FocusPanel = styled.div`
  padding: 22px;
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)), rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
`;
const FocusLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;
const FocusValue = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 28px;
  line-height: 1;
  font-weight: 1000;
`;
const FocusHint = styled.div`
  margin-top: 10px;
  color: #cbd5e1;
  font-size: 13px;
  line-height: 1.6;
`;
const StatsGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  @media (max-width: 920px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const StatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;
const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 800;
`;
const StatValue = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 28px;
  line-height: 1;
  font-weight: 1000;
`;
const StatHint = styled.div`
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
`;
const LegendRow = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;
const LegendPill = styled.div`
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  color: ${({ $tone }) =>
    $tone === "today" ? "#fde68a" : $tone === "completed" ? "#bbf7d0" : $tone === "missed" ? "#fecaca" : "#cbd5e1"};
  background: ${({ $tone }) =>
    $tone === "today" ? "rgba(245,158,11,0.14)" : $tone === "completed" ? "rgba(34,197,94,0.14)" : $tone === "missed" ? "rgba(239,68,68,0.14)" : "rgba(148,163,184,0.12)"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "today"
        ? "rgba(245, 158, 11, 0.22)"
        : $tone === "completed"
        ? "rgba(34, 197, 94, 0.22)"
        : $tone === "missed"
        ? "rgba(239, 68, 68, 0.22)"
        : "rgba(148, 163, 184, 0.18)"};
`;
const LegendCopy = styled.div`
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
`;
const MonthSection = styled.div`
  max-width: 1140px;
  margin: 0 auto 32px;
  padding: 20px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
`;
const MonthHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 14px;
  @media (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const MonthTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 900;
  color: #f8fafc;
`;
const MonthSub = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
`;
const MonthBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.12);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
`;
const GridScroller = styled.div`overflow-x:auto;`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(44px, 1fr));
  gap: 10px;
  min-width: 720px;
`;
const DayHeader = styled.div`
  color: #94a3b8;
  font-size: 12px;
  font-weight: 900;
  padding-bottom: 6px;
`;
const DayCell = styled.button`
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: ${({ status }) =>
    status === "today" ? "rgba(245,158,11,0.16)" : status === "completed" ? "rgba(34,197,94,0.14)" : status === "missed" ? "rgba(239,68,68,0.12)" : "rgba(15,23,42,0.55)"};
  border-radius: 16px;
  padding: 12px;
  min-height: 120px;
  text-align: left;
  cursor: pointer;
  color: #e2e8f0;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(125, 211, 252, 0.24);
  }
`;
const EmptyCell = styled.div`
  min-height: 120px;
`;
const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;
const DateNumber = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 900;
`;
const DayCount = styled.div`
  color: #94a3b8;
  font-size: 10px;
  font-weight: 900;
`;
const Topic = styled.div`
  margin-top: 8px;
  color: #f8fafc;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.45;
`;
const MotivationText = styled.div`
  font-size: 11px;
  font-weight: 800;
  margin-top: 6px;
  color: ${({ status }) =>
    status === "completed" ? "#22c55e" : status === "today" ? "#3b82f6" : status === "missed" ? "#ef4444" : "#94a3b8"};
`;
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.74);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
`;
const DetailCard = styled.div`
  width: min(620px, 100%);
  padding: 24px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.96);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
`;
const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;
const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;
const DetailBody = styled.div`
  margin-top: 18px;
`;
const DateText = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 13px;
`;
const CloseBtn = styled.button`
  border: none;
  background: rgba(148, 163, 184, 0.12);
  color: #e2e8f0;
  border-radius: 12px;
  width: 36px;
  height: 36px;
  cursor: pointer;
`;
const TopicTitle = styled.h3`
  margin: 0;
  color: #f8fafc;
  font-size: 22px;
`;
const DetailMetaGrid = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const DetailMetaCard = styled.div`
  padding: 16px;
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;
const DetailMetaLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;
const DetailMetaValue = styled.div`
  margin-top: 10px;
  color: #e2e8f0;
  font-size: 14px;
  line-height: 1.6;
  font-weight: 700;
`;
const MetaRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 14px;
`;
const StatusTag = styled.div`
  padding: 7px 10px;
  border-radius: 999px;
  background: ${({ status }) =>
    status === "completed"
      ? "rgba(34, 197, 94, 0.14)"
      : status === "today"
      ? "rgba(245, 158, 11, 0.14)"
      : status === "missed"
      ? "rgba(239, 68, 68, 0.14)"
      : "rgba(56, 189, 248, 0.12)"};
  color: ${({ status }) =>
    status === "completed"
      ? "#bbf7d0"
      : status === "today"
      ? "#fde68a"
      : status === "missed"
      ? "#fecaca"
      : "#bae6fd"};
  font-size: 11px;
  font-weight: 900;
`;
const DetailActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 18px;
`;
const PrimaryBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #111827;
  font-weight: 900;
  cursor: pointer;
`;
const SecondaryBtn = styled.button`
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 999px;
  padding: 12px 16px;
  background: rgba(148, 163, 184, 0.12);
  color: #e2e8f0;
  font-weight: 900;
  cursor: pointer;
`;
